/**
 * AIStatusCard Component (sl-n2xy)
 * Shows contextual messages when AI is not available.
 *
 * Replaces the old BYOK "AI not configured" card with managed-AI-aware states:
 * - credits_exhausted: Credits used up (owner sees buy link, member sees contact admin)
 * - no_server_keys: Platform AI not configured (temporary outage)
 * - server_key_decryption_failed: Platform AI keys cannot be decrypted
 * - free_plan: Plan upgrade needed
 * - disabled/suspended: Admin action needed
 */

import { WithDisposable } from '@blocksuite/affine/global/lit';
import { SettingsIcon } from '@blocksuite/icons/lit';
import { baseTheme } from '@streamlain/theme';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import { AffineIcon } from '../../_common/icons';

export type AIStatusReason =
  | 'credits_exhausted'
  | 'no_server_keys'
  | 'server_key_decryption_failed'
  | 'free_plan'
  | 'disabled'
  | 'suspended'
  | 'unknown'
  | null;

export class AINoKeysCard extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      font-family: ${unsafeCSS(baseTheme.fontSansFamily)};
    }

    .status-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 24px;
      max-width: 320px;
      text-align: center;
    }

    .status-icon {
      color: var(--streamlain-icon-secondary);
    }

    .status-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--streamlain-text-primary-color);
      margin: 0;
    }

    .status-description {
      font-size: var(--streamlain-font-sm);
      color: var(--streamlain-text-secondary-color);
      line-height: 1.5;
      margin: 0;
    }

    .status-button {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 8px;
      border: 1px solid var(--streamlain-border-color);
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: var(--streamlain-font-sm);
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .status-button:hover {
      background: var(--streamlain-hover-color);
      border-color: var(--streamlain-primary-color);
    }

    .status-button svg {
      width: 16px;
      height: 16px;
    }
  `;

  @property({ type: String, attribute: 'title' })
  override accessor title = '';

  @property({ type: String })
  accessor description = '';

  @property({ type: String })
  accessor buttonText = '';

  @property({ type: String })
  accessor reason: AIStatusReason = null;

  @property({ type: Boolean })
  accessor isOwner = false;

  @property({ attribute: false })
  accessor onOpenSettings: (() => void) | undefined;

  private readonly _handleClick = () => {
    this.onOpenSettings?.();
  };

  private _getTitle(): string {
    if (this.title) return this.title;
    switch (this.reason) {
      case 'credits_exhausted':
        return 'Credits exhausted';
      case 'no_server_keys':
        return 'AI temporarily unavailable';
      case 'server_key_decryption_failed':
        return 'AI configuration unavailable';
      case 'free_plan':
        return 'AI requires a paid plan';
      case 'disabled':
      case 'suspended':
        return 'AI is currently disabled';
      default:
        return 'AI not available';
    }
  }

  private _getDescription(): string {
    if (this.description) return this.description;
    switch (this.reason) {
      case 'credits_exhausted':
        return this.isOwner
          ? 'Your workspace has no remaining credits.'
          : 'Contact your workspace admin to purchase more credits.';
      case 'no_server_keys':
        return 'AI services are currently not available. Please try again later.';
      case 'server_key_decryption_failed':
        return 'The workspace AI key configuration cannot be decrypted. Contact an administrator.';
      case 'free_plan':
        return 'Upgrade to Pro or Team to use AI features.';
      case 'disabled':
        return 'AI has been disabled for this workspace.';
      case 'suspended':
        return 'AI has been suspended for this workspace. Contact support.';
      default:
        return 'AI features are not available at this time.';
    }
  }

  private _getButtonText(): string | null {
    if (this.buttonText) return this.buttonText;
    switch (this.reason) {
      case 'credits_exhausted':
        return this.isOwner ? 'Buy more credits' : null;
      case 'free_plan':
        return 'View plans';
      default:
        return null;
    }
  }

  override render() {
    const title = this._getTitle();
    const description = this._getDescription();
    const buttonText = this._getButtonText();

    return html`
      <div class="status-card" data-testid="ai-status-card">
        <div class="status-icon">
          ${AffineIcon('var(--streamlain-icon-secondary)')}
        </div>
        <h3 class="status-title">${title}</h3>
        <p class="status-description">${description}</p>
        ${buttonText
          ? html`
              <button
                class="status-button"
                @click=${this._handleClick}
                data-testid="ai-status-button"
              >
                ${SettingsIcon()}
                <span>${buttonText}</span>
              </button>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-no-keys-card': AINoKeysCard;
  }
}
